import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { mockNews } from '../../mocks/news';
import { mockTestimonials } from '../../mocks/testimonials';
import { mockFaq } from '../../mocks/faq';

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [openFaqId, setOpenFaqId] = useState<number | null>(null);
  
  const heroImages = [
    'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/b4b14b955616608c29c35a331517c9b4.jpeg',
    'https://public.readdy.ai/ai/img_res/4b46335d47863f502123a8b4a5325abf.jpg',
    'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/9cac033b226c138882b64846dd70b766.jpeg'
  ];

  const servicesAnimation = useScrollAnimation();
  const newsAnimation = useScrollAnimation();
  const testimonialsAnimation = useScrollAnimation();
  const faqAnimation = useScrollAnimation();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 7000);
    return () => clearInterval(interval);
  }, []);

  const toggleFaq = (id: number) => {
    setOpenFaqId(openFaqId === id ? null : id);
  };

  useEffect(() => {
    const siteUrl = typeof VITE_SITE_URL !== 'undefined' ? VITE_SITE_URL : 'https://example.com';
    
    document.title = '岡山市の英会話教室｜子供から大人まで楽しく学べる｜おかやま英会話教室株式会社';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '岡山市北区の英会話教室。子供から大人まで、オンラインレッスンも対応。ダンスと英語を組み合わせた独自のキッズコースで楽しく学べます。無料体験レッスン受付中。経験豊富な講師陣が一人ひとりのペースに合わせて丁寧に指導します。');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', '岡山市英会話教室,岡山英会話スクール,子供英会話岡山,オンライン英語レッスン,ダンス英語教室');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${siteUrl}/`);
    }
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', `${siteUrl}/`);
    }
    
    // Add Schema.org JSON-LD for Organization and WebSite
    const existingSchema = document.querySelector('script[type="application/ld+json"][data-page-schema]');
    if (existingSchema) {
      existingSchema.remove();
    }
    
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "EducationalOrganization",
          "@id": `${siteUrl}/#organization`,
          "name": "おかやま英会話教室株式会社",
          "url": `${siteUrl}/`,
          "logo": `${siteUrl}/logo.png`,
          "description": "岡山市北区の英会話教室。子供から大人まで、オンラインレッスンも対応。ダンスと英語を組み合わせた独自のキッズコースで楽しく学べます。",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "○○町1-2-3",
            "addressLocality": "岡山市北区",
            "addressRegion": "岡山県",
            "postalCode": "700-0000",
            "addressCountry": "JP"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "34.6617",
            "longitude": "133.9350"
          },
          "telephone": "+81-86-012-3456",
          "faxNumber": "+81-86-012-3457",
          "email": "info@okayama-english.jp",
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
            "opens": "09:00",
            "closes": "18:00"
          },
          "foundingDate": "2023",
          "numberOfEmployees": 9
        },
        {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          "url": `${siteUrl}/`,
          "name": "おかやま英会話教室株式会社",
          "description": "岡山市の英会話教室。子供から大人まで楽しく学べる英語教育を提供。",
          "publisher": {
            "@id": `${siteUrl}/#organization`
          },
          "inLanguage": "ja"
        },
        {
          "@type": "WebPage",
          "@id": `${siteUrl}/#webpage`,
          "url": `${siteUrl}/`,
          "name": "岡山市の英会話教室｜子供から大人まで楽しく学べる｜おかやま英会話教室株式会社",
          "isPartOf": {
            "@id": `${siteUrl}/#website`
          },
          "about": {
            "@id": `${siteUrl}/#organization`
          },
          "description": "岡山市北区の英会話教室。子供から大人まで、オンラインレッスンも対応。ダンスと英語を組み合わせた独自のキッズコースで楽しく学べます。",
          "inLanguage": "ja"
        }
      ]
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page-schema', 'true');
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);
  }, []);

  return (
    <>
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-screen overflow-hidden">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image}
              alt="おかやま英会話教室"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          </div>
        ))}
        
        <div className="relative z-10 h-full flex items-end justify-center text-white pb-32">
          <div className="text-center max-w-4xl mx-auto px-4">
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              岡山で選ばれる英会話教室
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl mb-12 font-light leading-relaxed">
              子どもから大人まで、あなたのペースで世界とつながる
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link
                to="/service"
                className="bg-coral-500 hover:bg-coral-600 text-white px-8 py-4 rounded-xl text-lg font-medium transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                サービス内容を見る
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white hover:bg-white hover:text-navy-900 px-8 py-4 rounded-xl text-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                無料体験レッスン予約
              </Link>
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
          <i className="ri-arrow-down-line text-3xl"></i>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="py-20 bg-white">
        <div 
          ref={servicesAnimation.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${servicesAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="text-center mb-16">
            <span className="bg-coral-500 text-white px-4 py-2 rounded-full text-sm font-medium">
              OUR SERVICES
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-900 mt-6 mb-4">
              あなたに合った学び方を
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 flex items-center justify-center bg-mint-500/10 rounded-full mb-6">
                <i className="ri-graduation-cap-line text-3xl text-mint-500"></i>
              </div>
              <h3 className="font-bold text-xl text-navy-900 mb-4">中高生英会話</h3>
              <p className="text-gray-600 leading-relaxed">
                受験英語から実践的な会話まで幅広くサポート。定期テスト対策や英検対策も充実しています。
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 flex items-center justify-center bg-coral-500/10 rounded-full mb-6">
                <i className="ri-user-line text-3xl text-coral-500"></i>
              </div>
              <h3 className="font-bold text-xl text-navy-900 mb-4">大人の英会話</h3>
              <p className="text-gray-600 leading-relaxed">
                ビジネスから日常会話まで、目的に応じたカリキュラムで効率よく英語力を向上させます。
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 flex items-center justify-center bg-mint-500/10 rounded-full mb-6">
                <i className="ri-computer-line text-3xl text-mint-500"></i>
              </div>
              <h3 className="font-bold text-xl text-navy-900 mb-4">オンラインレッスン</h3>
              <p className="text-gray-600 leading-relaxed">
                自宅にいながら質の高いレッスンを受講。忙しい方でも続けやすいフレキシブルなシステムです。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-20 bg-beige-50">
        <div 
          ref={newsAnimation.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${newsAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="mb-12">
            <span className="bg-navy-900 text-white px-4 py-2 rounded-full text-sm font-medium">
              NEWS
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy-900 mt-6 mb-4">
              最新のお知らせ
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {mockNews.map((news) => (
              <article key={news.id} className="bg-white rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 text-center">
                    <div className="text-coral-500 font-bold text-lg">
                      {news.date.split('.')[1]}.{news.date.split('.')[2]}
                    </div>
                    <div className="text-coral-500 font-bold text-sm">
                      {news.date.split('.')[0]}
                    </div>
                  </div>
                  <div className="flex-1">
                    <span className="inline-block bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-medium mb-3">
                      {news.category}
                    </span>
                    <h3 className="text-navy-900 font-semibold text-lg mb-2 leading-snug">
                      {news.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {news.content}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div 
          ref={testimonialsAnimation.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${testimonialsAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="text-center mb-16">
            <span className="bg-mint-500 text-white px-4 py-2 rounded-full text-sm font-medium">
              TESTIMONIALS
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-900 mt-6 mb-4">
              受講生の声
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mockTestimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-gradient-to-br from-beige-50 to-white rounded-2xl p-8 relative">
                <div className="flex items-center mb-6">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h3 className="font-bold text-navy-900 text-lg">{testimonial.name}</h3>
                    <p className="text-gray-600 text-sm">{testimonial.age}</p>
                  </div>
                </div>
                <div className="absolute top-6 right-6 text-mint-500/20">
                  <i className="ri-double-quotes-r text-4xl"></i>
                </div>
                <p className="text-navy-900 leading-relaxed">
                  {testimonial.content}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-beige-50">
        <div 
          ref={faqAnimation.ref}
          className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${faqAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="text-center mb-12">
            <span className="bg-coral-500 text-white px-4 py-2 rounded-full text-sm font-medium">
              FAQ
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy-900 mt-6 mb-4">
              よくある質問
            </h2>
          </div>
          
          <div className="space-y-4">
            {mockFaq.map((faq) => (
              <div key={faq.id} className="bg-white rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleFaq(faq.id)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="font-semibold text-navy-900 text-lg pr-4">
                    {faq.question}
                  </span>
                  <i className={`ri-${openFaqId === faq.id ? 'subtract' : 'add'}-line text-2xl text-coral-500 transform transition-transform duration-300 ${openFaqId === faq.id ? 'rotate-180' : ''}`}></i>
                </button>
                <div className={`px-8 transition-all duration-300 overflow-hidden ${openFaqId === faq.id ? 'pb-6 max-h-96' : 'max-h-0'}`}>
                  <p className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default HomePage;